import React, { useState, useEffect, useCallback } from 'react';
import MainService from '../../services/MainService';
import { RefreshCw, Download } from 'lucide-react';

function localDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return {
    start: localDateInput(start),
    end: localDateInput(end),
  };
}

/** Interpret `yyyy-mm-dd` as the user's local calendar day; return UTC ISO bounds for the API. */
function localDayStartToUtcIso(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  const localStart = new Date(y, m - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(localStart.getTime())) return null;
  return localStart.toISOString();
}

function localDayEndToUtcIso(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  const localEnd = new Date(y, m - 1, d, 23, 59, 59, 999);
  if (Number.isNaN(localEnd.getTime())) return null;
  return localEnd.toISOString();
}

/**
 * DB/API use UTC; JSON often omitted "Z" (DateTime Unspecified), so `new Date(iso)` was parsed as local wall time.
 */
function parseActivityUtc(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let s = String(value).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const d = new Date(parseInt(s, 10, 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const hasZone = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasZone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    s = `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local calendar + clock: d-MM-yyyy, h:mm am/pm (avoids US MM/DD and 24h from toLocaleString). */
function formatLocalDateTime(isoUtc) {
  const d = parseActivityUtc(isoUtc);
  if (!d) return '—';
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const h24 = d.getHours();
  const min = d.getMinutes();
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  const ampm = h24 >= 12 ? 'pm' : 'am';
  const mm = String(min).padStart(2, '0');
  return `${day}-${String(month).padStart(2, '0')}-${year}, ${h12}:${mm} ${ampm}`;
}

/** Duration in ms → numeric seconds string (no suffix); empty if missing. */
function durationSecondsRaw(ms) {
  if (ms == null || ms === '') return '';
  const n = Number(ms);
  if (Number.isNaN(n)) return '';
  const sec = n / 1000;
  if (sec === 0) return '0';
  return sec.toFixed(3).replace(/\.?0+$/, '');
}

/** Duration stored in ms; display in seconds with unit. */
function formatDurationSeconds(ms) {
  const v = durationSecondsRaw(ms);
  if (v === '') return '—';
  return `${v} s`;
}

function pagedFromApi(res) {
  const totalCount = res?.totalCount ?? res?.TotalCount ?? 0;
  const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.Items) ? res.Items : []);
  return { totalCount, items };
}

function rowVal(r, camel, pascal) {
  if (r == null) return undefined;
  if (r[camel] !== undefined && r[camel] !== null) return r[camel];
  return r[pascal];
}

function parseAdditionalData(r) {
  const raw = rowVal(r, 'additionalData', 'AdditionalData');
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return null;
  }
}

/** Button / control label for UI click rows (stored in AdditionalData JSON). */
function buttonLabelFromRow(r) {
  const o = parseAdditionalData(r);
  if (!o) return '';
  const t = String(o.activityType || o.ActivityType || '').toLowerCase();
  if (t !== 'click') return '';
  const label = (o.actionName || o.ActionName || '').trim();
  if (label) return label;
  const ctrl = (o.controlName || o.ControlName || '').trim();
  return ctrl || '';
}

function countryRegionDisplay(r) {
  const cr = rowVal(r, 'countryRegion', 'CountryRegion');
  const reg = rowVal(r, 'region', 'Region');
  if (cr && reg) return `${cr} (${reg})`;
  return cr || reg || '';
}

function dash(v) {
  if (v == null || v === '') return '—';
  return v;
}

export default function ActivityTrackerReport() {
  const [{ start, end }, setRange] = useState(defaultRange);
  const [filters, setFilters] = useState({
    userName: '',
    page: '',
    deviceType: '',
    ipAddress: '',
    countryRegion: '',
    region: '',
    browserOs: '',
    searchText: '',
  });
  const [sortField, setSortField] = useState('CreatedAtUtc');
  const [sortDesc, setSortDesc] = useState(true);
  const [skip, setSkip] = useState(0);
  const take = 100;
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadOptions = useCallback(async () => {
    try {
      const o = await MainService.ActivityTrackerFilterOptions();
      const normalized = o
        ? {
            userNames: o.userNames || o.UserNames || [],
            pages: o.pages || o.Pages || [],
            deviceTypes: o.deviceTypes || o.DeviceTypes || [],
            ipAddresses: o.ipAddresses || o.IpAddresses || [],
            countryRegions: o.countryRegions || o.CountryRegions || [],
            regions: o.regions || o.Regions || [],
            browserOss: o.browserOss || o.BrowserOss || [],
          }
        : {};
      setOptions(normalized);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const body = {
        startDate: localDayStartToUtcIso(start),
        endDate: localDayEndToUtcIso(end),
        userName: filters.userName || null,
        page: filters.page || null,
        deviceType: filters.deviceType || null,
        ipAddress: filters.ipAddress || null,
        countryRegion: filters.countryRegion || null,
        region: filters.region || null,
        browserOs: filters.browserOs || null,
        searchText: filters.searchText || null,
        sortField,
        sortDesc,
        skip,
        take,
      };
      const res = await MainService.ActivityTrackerInView(body);
      const { totalCount, items } = pagedFromApi(res);
      setTotal(totalCount);
      setRows(items);
    } catch (e) {
      const detail = e && e.message ? ` ${e.message}` : '';
      setError(`Could not load activity data. Deploy the latest API, stay signed in, and widen the date range if needed.${detail}`);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [start, end, filters, sortField, sortDesc, skip, take]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const exportCsv = async () => {
    setLoading(true);
    try {
      const all = [];
      let s = 0;
      const pageSize = 500;
      let more = true;
      while (more) {
        const body = {
          startDate: localDayStartToUtcIso(start),
          endDate: localDayEndToUtcIso(end),
          userName: filters.userName || null,
          page: filters.page || null,
          deviceType: filters.deviceType || null,
          ipAddress: filters.ipAddress || null,
          countryRegion: filters.countryRegion || null,
          region: filters.region || null,
          browserOs: filters.browserOs || null,
          searchText: filters.searchText || null,
          sortField,
          sortDesc,
          skip: s,
          take: pageSize,
        };
        const res = await MainService.ActivityTrackerInView(body);
        const { items: chunk, totalCount } = pagedFromApi(res);
        all.push(...chunk);
        s += chunk.length;
        more = chunk.length === pageSize && s < totalCount;
      }

      const exportCols = [
        { h: 'DateTimeLocal', get: (r) => formatLocalDateTime(rowVal(r, 'createdAtUtc', 'CreatedAtUtc')) },
        { h: 'UserName', get: (r) => rowVal(r, 'userName', 'UserName') },
        { h: 'IpAddress', get: (r) => rowVal(r, 'ipAddress', 'IpAddress') },
        { h: 'VisitorName', get: (r) => rowVal(r, 'visitorName', 'VisitorName') },
        { h: 'DeviceName', get: (r) => rowVal(r, 'deviceName', 'DeviceName') },
        { h: 'DeviceType', get: (r) => rowVal(r, 'deviceType', 'DeviceType') },
        { h: 'CountryRegion', get: (r) => countryRegionDisplay(r) },
        { h: 'BrowserOs', get: (r) => rowVal(r, 'browserOs', 'BrowserOs') },
        { h: 'Page', get: (r) => rowVal(r, 'page', 'Page') },
        { h: 'Button', get: (r) => buttonLabelFromRow(r) },
        { h: 'PageUrl', get: (r) => rowVal(r, 'pageUrl', 'PageUrl') },
        { h: 'Referrer', get: (r) => rowVal(r, 'referrer', 'Referrer') },
        { h: 'HttpMethod', get: (r) => rowVal(r, 'httpMethod', 'HttpMethod') },
        { h: 'Endpoint', get: (r) => rowVal(r, 'endpoint', 'Endpoint') },
        { h: 'StatusCode', get: (r) => rowVal(r, 'statusCode', 'StatusCode') },
        { h: 'DurationSec', get: (r) => durationSecondsRaw(rowVal(r, 'durationMs', 'DurationMs')) },
        { h: 'ComputerName', get: (r) => rowVal(r, 'computerName', 'ComputerName') },
        { h: 'AdditionalData', get: (r) => rowVal(r, 'additionalData', 'AdditionalData') },
      ];
      const esc = (v) => {
        if (v == null) return '';
        const s0 = String(v).replace(/"/g, '""');
        return `"${s0}"`;
      };
      const header = exportCols.map((c) => c.h).join(',');
      const lines = all.map((r) => exportCols.map((c) => esc(c.get(r))).join(','));
      const csv = [header, ...lines].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-tracker-${start}-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / take));
  const pageIndex = Math.floor(skip / take) + 1;

  return (
    <div className="max-w-[100%] space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="page-title text-2xl font-semibold text-gray-900">Activity Tracker</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fetchPage()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="block text-sm">
            <span className="text-gray-600">From (local date)</span>
            <input
              type="date"
              value={start}
              onChange={(e) => { setSkip(0); setRange((r) => ({ ...r, start: e.target.value })); }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">To (local date)</span>
            <input
              type="date"
              value={end}
              onChange={(e) => { setSkip(0); setRange((r) => ({ ...r, end: e.target.value })); }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Sort</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="CreatedAtUtc">Time</option>
              <option value="UserName">User</option>
              <option value="Page">Page</option>
              <option value="DurationMs">Duration</option>
              <option value="StatusCode">Status</option>
              <option value="IpAddress">IP</option>
              <option value="HttpMethod">Method</option>
              <option value="Endpoint">Endpoint</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm mt-6">
            <input
              type="checkbox"
              checked={sortDesc}
              onChange={(e) => setSortDesc(e.target.checked)}
            />
            Descending
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ['userName', 'User', 'userNames'],
            ['page', 'Page', 'pages'],
            ['deviceType', 'Device', 'deviceTypes'],
            ['ipAddress', 'IP', 'ipAddresses'],
            ['countryRegion', 'Country', 'countryRegions'],
            ['region', 'Region', 'regions'],
            ['browserOs', 'Browser / OS', 'browserOss'],
          ].map(([key, label, optKey]) => (
            <label key={key} className="block text-sm">
              <span className="text-gray-600">{label}</span>
              <input
                list={`dl-${key}`}
                value={filters[key]}
                onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, [key]: e.target.value })); }}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Filter…"
              />
              {options && Array.isArray(options[optKey]) && (
                <datalist id={`dl-${key}`}>
                  {options[optKey].slice(0, 200).map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              )}
            </label>
          ))}
          <label className="block text-sm md:col-span-2">
            <span className="text-gray-600">Search text</span>
            <input
              value={filters.searchText}
              onChange={(e) => { setSkip(0); setFilters((f) => ({ ...f, searchText: e.target.value })); }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Page, user, endpoint, IP…"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => { setSkip(0); fetchPage(); }}
              className="w-full md:w-auto px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800"
            >
              Apply filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-[0_1px_0_0_rgb(229_231_235)]">
              <tr>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Date/Time (local)</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">User name</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">IP</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Visitor name</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Device name</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Device type</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Country / region</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 min-w-[10rem]">Browser / OS</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 min-w-[8rem]">Page</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 min-w-[7rem]">Button</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 min-w-[12rem]">Page URL</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 min-w-[8rem]">Referrer</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Method</th>
                <th className="text-left px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Status</th>
                <th className="text-right px-2 py-2 font-medium text-gray-600 whitespace-nowrap">Duration (s)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const t = rowVal(r, 'createdAtUtc', 'CreatedAtUtc');
                const timeStr = formatLocalDateTime(t);
                const durSec = formatDurationSeconds(rowVal(r, 'durationMs', 'DurationMs'));
                const pageUrl = rowVal(r, 'pageUrl', 'PageUrl') || '';
                const ref = rowVal(r, 'referrer', 'Referrer') || '';
                const btn = buttonLabelFromRow(r);
                return (
                  <tr key={rowVal(r, 'id', 'Id')} className="hover:bg-gray-50 align-top">
                    <td className="px-2 py-2 whitespace-nowrap text-gray-800 tabular-nums">{timeStr}</td>
                    <td className="px-2 py-2 text-gray-800 max-w-[7rem]">
                      <span className="block truncate" title={String(rowVal(r, 'userName', 'UserName') || '')}>
                        {dash(rowVal(r, 'userName', 'UserName'))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-700 whitespace-nowrap font-mono text-[11px]">
                      {dash(rowVal(r, 'ipAddress', 'IpAddress'))}
                    </td>
                    <td className="px-2 py-2 text-gray-800 max-w-[7rem]">
                      <span className="block truncate" title={String(rowVal(r, 'visitorName', 'VisitorName') || '')}>
                        {dash(rowVal(r, 'visitorName', 'VisitorName'))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-700 max-w-[8rem]">
                      <span
                        className="block truncate"
                        title={String(rowVal(r, 'deviceName', 'DeviceName') || rowVal(r, 'computerName', 'ComputerName') || '')}
                      >
                        {dash(rowVal(r, 'deviceName', 'DeviceName') || rowVal(r, 'computerName', 'ComputerName'))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
                      {dash(rowVal(r, 'deviceType', 'DeviceType'))}
                    </td>
                    <td className="px-2 py-2 text-gray-700 max-w-[9rem]">
                      <span className="block truncate" title={countryRegionDisplay(r)}>
                        {dash(countryRegionDisplay(r))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-700 max-w-[14rem]">
                      <span className="block truncate" title={String(rowVal(r, 'browserOs', 'BrowserOs') || '')}>
                        {dash(rowVal(r, 'browserOs', 'BrowserOs'))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-800 max-w-[12rem]">
                      <span className="block truncate" title={String(rowVal(r, 'page', 'Page') || '')}>
                        {dash(rowVal(r, 'page', 'Page'))}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-gray-800 max-w-[10rem]">
                      <span className="block truncate font-medium" title={btn || ''}>
                        {btn ? btn : '—'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-indigo-700 max-w-[20rem] break-all" title={pageUrl || undefined}>
                      {pageUrl ? pageUrl : '—'}
                    </td>
                    <td className="px-2 py-2 text-gray-600 max-w-[14rem] break-all" title={ref || undefined}>
                      {ref ? ref : '—'}
                    </td>
                    <td className="px-2 py-2 text-gray-700 whitespace-nowrap">
                      {dash(rowVal(r, 'httpMethod', 'HttpMethod'))}
                    </td>
                    <td className="px-2 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                      {rowVal(r, 'statusCode', 'StatusCode') ?? '—'}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-gray-800">
                      {durSec}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={15} className="px-3 py-10 text-center text-gray-500 text-sm">
                    No rows for this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50 text-sm text-gray-700">
          <span>
            {total} total · Page {pageIndex} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={skip <= 0 || loading}
              onClick={() => setSkip((s) => Math.max(0, s - take))}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={skip + take >= total || loading}
              onClick={() => setSkip((s) => s + take)}
              className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
